import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import { API_BASE } from "../../lib/authApi";
import "./pulse-designer.css";

async function toJson(res: Response) {
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const error = new Error(`Public Media Kit request failed (${res.status})`) as Error & { status: number; data: unknown };
    error.status = res.status; error.data = data;
    throw error;
  }
  return { data, status: res.status };
}

// Bare fetch intentionally prevents a logged-in visitor's bearer token from
// being sent to sponsor-facing endpoints.
const publicApi = {
  get: (url: string) => fetch(url).then(toJson),
  post: (url: string, body: unknown) => fetch(url, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  }).then(toJson),
};

type Metric = { value: number | null; available: boolean };
type Point = { label: string; value: string };
type Visibility = {
  profile: boolean; bio_intro: boolean; metrics_subscribers: boolean;
  metrics_avg_views_per_video: boolean; metrics_engagement: boolean;
  metrics_audience_demographics: boolean; iris_partnership_fit: boolean;
};
type MediaKit = {
  name: string | null; pulse_handle: string; channel_handle: string | null;
  email: string | null; tags: string[]; bio: string | null;
  avatar_url: string | null; cover_url: string | null; logo_url: string | null;
  platform: "youtube" | "instagram"; visibility: Visibility;
  metrics: {
    subscribers: Metric; total_views: Metric; avg_monthly_reach: Metric;
    avg_views_per_video: Metric; engagement: Metric; audience_demographics: Metric;
    audience_age_distribution: Point[]; audience_geo_distribution: Point[];
    top_content_subtitle: string;
    top_content: Array<{ title: string; views: number; engagement_rate: number; url?: string }>;
  };
  partnership_fit: { summary: string; action: string } | null;
};
type Payload = { require_passcode: boolean; require_approved_email: boolean; content: MediaKit | null };

const ALL_VISIBLE: Visibility = {
  profile: true, bio_intro: true, metrics_subscribers: true,
  metrics_avg_views_per_video: true, metrics_engagement: true,
  metrics_audience_demographics: true, iris_partnership_fit: true,
};
const metric = (raw: any): Metric => ({
  value: raw?.value == null ? null : Number(raw.value), available: Boolean(raw?.available),
});
const normalizeContent = (raw: any): MediaKit | null => {
  if (!raw) return null;
  const metrics = raw.metrics ?? {};
  return {
    name: raw.name ?? null,
    pulse_handle: String(raw.pulse_handle || "").replace(/^@/, ""),
    channel_handle: raw.channel_handle ? String(raw.channel_handle).replace(/^@/, "") : null,
    email: raw.email ?? null,
    tags: Array.isArray(raw.tags) ? raw.tags.filter(Boolean) : [],
    bio: raw.bio ?? raw.description ?? null,
    avatar_url: raw.avatar_url ?? null, cover_url: raw.cover_url ?? null, logo_url: raw.logo_url ?? null,
    platform: raw.platform === "instagram" ? "instagram" : "youtube",
    visibility: { ...ALL_VISIBLE, ...(raw.visibility ?? {}) },
    metrics: {
      subscribers: metric(metrics.subscribers), total_views: metric(metrics.total_views),
      avg_monthly_reach: metric(metrics.avg_monthly_reach), avg_views_per_video: metric(metrics.avg_views_per_video),
      engagement: metric(metrics.engagement), audience_demographics: metric(metrics.audience_demographics),
      audience_age_distribution: Array.isArray(metrics.audience_age_distribution) ? metrics.audience_age_distribution : [],
      audience_geo_distribution: Array.isArray(metrics.audience_geo_distribution) ? metrics.audience_geo_distribution : [],
      top_content_subtitle: metrics.top_content_subtitle || "No qualifying YouTube uploads found in the selected lookback.",
      top_content: Array.isArray(metrics.top_content) ? metrics.top_content : [],
    },
    partnership_fit: raw.partnership_fit ?? null,
  };
};
const normalizeResponse = (data: any): Payload => ({
  require_passcode: Boolean(data?.require_passcode),
  require_approved_email: Boolean(data?.require_approved_email),
  content: normalizeContent(data?.content !== undefined ? data.content : data?.media_kit ?? data?.channel),
});

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "P";
const compact = (value?: number | null) => value == null ? "--" : Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(Math.max(0, value));
const percent = (value: string) => Math.max(0, Math.min(100, Number.parseFloat(value) || 0));
const mediaUrl = (value?: string | null) => !value || /^(data:|https?:|blob:)/i.test(value)
  ? value || "" : `${API_BASE.replace(/\/$/, "")}/${value.replace(/^\//, "")}`;
const Empty: React.FC<{ label: string }> = ({ label }) => <p className="pulse-v2-empty-metric">{label}</p>;

const PulsePublicMediaKit: React.FC = () => {
  const { slug = "" } = useParams();
  const [payload, setPayload] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [passcodeOk, setPasscodeOk] = useState(false);
  const [emailOk, setEmailOk] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setNotFound(false); setPayload(null);
    publicApi.get(`${API_BASE}/api/public/media-kit/${encodeURIComponent(slug)}`)
      .then(({ data }) => {
        if (cancelled) return;
        const next = normalizeResponse(data);
        setPayload(next); setPasscodeOk(!next.require_passcode); setEmailOk(!next.require_approved_email);
      })
      .catch((reason) => { if (!cancelled) reason?.status === 404 ? setNotFound(true) : setPayload(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const unlock = async (kind: "passcode" | "email") => {
    setBusy(true); setError("");
    try {
      const path = kind === "passcode" ? "unlock-passcode" : "verify-email";
      const body = kind === "passcode" ? { passcode } : { email: email.trim().toLowerCase() };
      const { data } = await publicApi.post(`${API_BASE}/api/public/media-kit/${encodeURIComponent(slug)}/${path}`, body);
      setPayload(normalizeResponse(data)); setPasscodeOk(true); setEmailOk(true);
    } catch {
      setError(kind === "passcode" ? "Incorrect passcode. Try again." : "That email isn't approved to view this media kit.");
    } finally { setBusy(false); }
  };

  return <div className="pulse-public-shell"><main className="pulse-v2-page pulse-v2-media pulse-public-page">
    {loading ? <section className="pulse-v2-card pulse-public-state">Loading media kit…</section>
      : notFound ? <section className="pulse-v2-card pulse-public-state">This media kit link is no longer available.</section>
      : !payload ? <section className="pulse-v2-card pulse-public-state">Something went wrong loading this media kit.</section>
      : !passcodeOk ? <Gate icon={<LockOutlinedIcon fontSize="large" />} title="This media kit is protected" help="Enter the passcode the creator shared with you." type="password" value={passcode} placeholder="Passcode" button="Unlock" busy={busy} error={error} onChange={setPasscode} onSubmit={() => unlock("passcode")} />
      : !emailOk ? <Gate icon={<MailOutlineRoundedIcon fontSize="large" />} title="Verify your email to continue" help="Only email addresses the creator approved can view this media kit." type="email" value={email} placeholder="name@brand.com" button="Continue" busy={busy} error={error} onChange={setEmail} onSubmit={() => unlock("email")} />
      : payload.content ? <PublicView content={payload.content} />
      : <section className="pulse-v2-card pulse-public-state">This media kit isn't available right now.</section>}
  </main></div>;
};

const Gate: React.FC<{ icon: React.ReactNode; title: string; help: string; type: string; value: string; placeholder: string; button: string; busy: boolean; error: string; onChange: (value: string) => void; onSubmit: () => void }> = (props) => (
  <section className="pulse-v2-card pulse-public-gate">{props.icon}<h2>{props.title}</h2><p>{props.help}</p>
    <div className="pulse-public-gate-form"><input type={props.type} value={props.value} placeholder={props.placeholder} onChange={(event) => props.onChange(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") props.onSubmit(); }} /><button type="button" disabled={props.busy || !props.value} onClick={props.onSubmit}>{props.busy ? "Checking…" : props.button}</button></div>
    {props.error && <p className="pulse-public-gate-error" role="alert">{props.error}</p>}
  </section>
);

const PublicView: React.FC<{ content: MediaKit }> = ({ content }) => {
  const { visibility: v, metrics: m } = content;
  const name = content.name || "Creator";
  // The route remains keyed by the Pulse profile handle; the visible handle is
  // the connected channel selected by this share slug.
  const channelHandle = content.channel_handle ? `@${content.channel_handle}` : "";
  const ages = m.audience_age_distribution.slice(0, 5);
  const geos = m.audience_geo_distribution.slice(0, 5);
  return <>
    {v.profile && <section className="pulse-v2-media-hero">
      <div className="pulse-v2-media-cover" style={content.cover_url ? { backgroundImage: `linear-gradient(0deg, rgba(13, 10, 24, .18), rgba(13, 10, 24, 0)), url(${JSON.stringify(mediaUrl(content.cover_url))})` } : undefined}>{content.logo_url && <img className="pulse-review-brand-logo" src={mediaUrl(content.logo_url)} alt="Creator logo" />}</div>
      <div className="pulse-v2-media-profile-row"><div className="pulse-v2-media-identity">
        <span className="pulse-v2-avatar large">{content.avatar_url ? <img src={mediaUrl(content.avatar_url)} alt="" referrerPolicy="no-referrer" /> : initials(name)}</span>
        <h2>{name}</h2>{channelHandle && <p className="pulse-public-channel-handle">{channelHandle}</p>}{content.email && <p>{content.email}</p>}
        {content.tags.length > 0 && <div className="pulse-v2-media-tags">{content.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>}
        {v.bio_intro && content.bio && <p className="pulse-v2-media-bio">{content.bio}</p>}
      </div></div>
    </section>}
    {(v.metrics_subscribers || v.metrics_avg_views_per_video || v.metrics_engagement) && <section className="pulse-v2-card pulse-v2-channel-evidence">
      <h2><span>{content.platform === "instagram" ? "Instagram" : "YouTube"}</span>{content.platform === "youtube" ? " - Long-form" : ""}</h2><div>
        {v.metrics_subscribers && <article><span>{content.platform === "instagram" ? "Followers" : "Subscribers"}</span><strong>{compact(m.subscribers.value)}</strong><small>Current public total</small></article>}
        {v.metrics_avg_views_per_video && <article><span>Total views</span><strong>{compact(m.total_views.value)}</strong><small>Current public total</small></article>}
        {v.metrics_avg_views_per_video && <article><span>Avg. monthly reach</span><strong>{compact(m.avg_monthly_reach.value)}</strong><small>Connected analytics</small></article>}
        {v.metrics_engagement && <article><span>Engagement rate</span><strong>{m.engagement.value == null ? "--" : `${m.engagement.value.toFixed(1)}%`}</strong><small>{m.engagement.available ? "Measured from connected data" : "Data pending"}</small></article>}
      </div>
    </section>}
    {v.metrics_audience_demographics && <div className="pulse-v2-demographic-grid">
      <section className="pulse-v2-card"><h2>Age Distribution</h2>{ages.length ? ages.map((row) => <div className="pulse-v2-demographic-row" key={row.label}><span>{row.label}</span><i><b style={{ width: `${percent(row.value)}%` }} /></i><strong>{row.value}</strong></div>) : <Empty label="Age data pending" />}</section>
      <section className="pulse-v2-card"><h2>Geographic Reach</h2>{geos.length ? geos.map((row) => <div className="pulse-v2-demographic-row" key={row.label}><span>{row.label}</span><i><b style={{ width: `${percent(row.value)}%` }} /></i><strong>{row.value}</strong></div>) : <Empty label="Geography data pending" />}</section>
    </div>}
    {v.iris_partnership_fit && <section className="pulse-v2-partnership"><h2><AutoAwesomeRoundedIcon fontSize="small" /> Partnership Fit</h2><p>{content.partnership_fit?.summary || "IRIS is gathering enough connected-channel evidence to write a trustworthy partnership fit."} {content.partnership_fit?.action || ""}</p></section>}
    <section className="pulse-v2-card pulse-v2-top-content"><h2>Top Performing Content</h2><p>{m.top_content_subtitle}</p>{m.top_content.length ? m.top_content.slice(0, 3).map((item) => <article key={`${item.title}-${item.url || ""}`}><div><strong>{item.url ? <a href={item.url} target="_blank" rel="noreferrer">{item.title}</a> : item.title}</strong><span>{compact(item.views)} views - {item.engagement_rate.toFixed(1)}% engagement</span></div></article>) : <Empty label="No qualifying videos in this period" />}</section>
  </>;
};

export default PulsePublicMediaKit;
